import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import Headline from "../../components/Headline";
import { MainLayout } from "../../components/Layout";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import SmartInput from "../../components/SmartInput";
import SmartButton from "../../components/SmartButton";
import "./register-address-styles.scss";
import {
    CardPaymentPlan,
    Prefecture,
    UserAddress,
    UserAddressParams
} from "../../generated/UserAPIAgent";
import { PREFECTURES } from "../../generated/lib-domain/prefectures";
import { PlanPrice } from "../../components/PlanPrice";
import { PaymentType } from "src/generated/lib-domain/paymentZeus";
import { PaymentMethod } from "src/generated/lib-domain/iap";
import PostalCodeButton from "../../components/PostalCodeButton";

export interface PaymentInfoState {
    paymentType: PaymentType | null;
    orderedAmount: number | null;
    firstAmount: number | null;
    paymentPlanId: number | null;
    primaryPaymentMethod: PaymentMethod;
}

interface RegisterAddressPageProps {
    onSubmitAddress: (
        zip: string,
        pref: Prefecture,
        city: string,
        address: string,
        building?: string
    ) => Promise<void>;
    isLoading: boolean;
    userAddress: UserAddress | null;
    paymentType: PaymentType | null;
    cardPaymentPlan: CardPaymentPlan;
}

const schema = yup.object().shape({
    zip: yup
        .string()
        .required("郵便番号が入力されていません")
        .matches(/^[\d]{7}$/, "郵便番号は7桁の半角数字のみでご記入ください"),
    pref: yup.number(),
    city: yup
        .string()
        .required("市区町村が入力されていません")
        .max(48, "市区町村は48文字以内で入力してください"),
    address: yup
        .string()
        .required("番地が入力されていません")
        .max(25, "番地は25文字以内で入力してください"),
    building: yup.string().max(25, "建物名は25文字以内で入力してください")
});

const RegisterAddress = (props: RegisterAddressPageProps) => {
    const {
        register,
        handleSubmit,
        errors,
        formState: { isValid },
        getValues,
        setValue
    } = useForm({
        mode: "all",
        resolver: yupResolver(schema)
    });

    const defaultUserAddress: UserAddressParams = {
        address: props.userAddress != null ? props.userAddress.address : "",
        building: props.userAddress != null ? props.userAddress.building : null,
        city: props.userAddress != null ? props.userAddress.city : "",
        pref:
            props.userAddress != null
                ? props.userAddress.pref
                : (0 as Prefecture), // TODO: fix
        zip: props.userAddress != null ? props.userAddress.zip : ""
    };

    const onSubmit = async (data) => {
        await props.onSubmitAddress(
            data.zip,
            data.pref,
            data.city,
            data.address,
            data.building
        );
    };

    const enterAddressFromPostalCode = (address: {
        region_id: number;
        region: string;
        locality: string;
        street: string;
        extended: string;
    }) => {
        if (address.region_id) {
            setValue("pref", address.region_id);
            setValue("city", address.locality + address.street);
            setValue("address", address.extended);
        }
    };

    return (
        <MainLayout>
            <Headline>有料契約書面の郵送先</Headline>
            <section className="register-address">
                <div className="description">
                    <p>
                        有料契約完了後に契約書面をお送り致しますので、お届け先をご入力ください。
                        <br />
                        なお、契約書面はプライバシーを考慮して無地の封筒に封入し、差出人は個人名で送付致します。
                    </p>
                </div>
                <div className="description-accent-pink">
                    <p>
                        既にアプリからご入力いただき、受取り済み、または受取り待ちの状態の方は入力が不要です。そのまま次へお進みください。
                    </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="smart-input-wrapper">
                        <label>郵便番号</label>
                        <SmartInput
                            name="zip"
                            register={register}
                            error={errors.zip}
                            placeholder="1230000（ハイフン無し）"
                            className="address-input"
                            defaultValue={defaultUserAddress.zip}
                        ></SmartInput>
                    </div>
                    <PostalCodeButton
                        postalCode={getValues("zip")}
                        handleOnClick={enterAddressFromPostalCode}
                    />
                    <div className="smart-input-wrapper">
                        <label className="address-label">郵送先住所</label>
                        <div className="address-input prefecture-select">
                            <select
                                name="pref"
                                ref={register()}
                                required
                                defaultValue={defaultUserAddress.pref}
                            >
                                <option value="">
                                    都道府県を選択してください
                                </option>
                                {PREFECTURES.map((p) => {
                                    return (
                                        <option value={p.id} key={p.id}>
                                            {p.text}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <SmartInput
                            name="city"
                            register={register}
                            error={errors.city}
                            placeholder="市区町村"
                            className="address-input"
                            defaultValue={defaultUserAddress.city}
                        ></SmartInput>
                        <SmartInput
                            name="address"
                            register={register}
                            error={errors.address}
                            placeholder="番地"
                            className="address-input"
                            defaultValue={defaultUserAddress.address}
                        ></SmartInput>
                        <SmartInput
                            name="building"
                            register={register}
                            error={errors.building}
                            placeholder="建物名、号室"
                            className="address-input"
                            defaultValue={defaultUserAddress.building}
                        ></SmartInput>
                    </div>
                    <div className="description-accent-red">
                        <p>
                            郵送先住所は弊社申請・郵送のために使用します。
                            <br />
                            一切公開されることはございません。
                        </p>
                    </div>
                    <div>
                        <SmartButton
                            type="submit"
                            disabled={!isValid}
                            isLoading={props.isLoading}
                        >
                            <PlanPrice
                                cardPaymentPlan={props.cardPaymentPlan}
                            />{" "}
                            購入する
                        </SmartButton>
                    </div>
                </form>
            </section>
        </MainLayout>
    );
};

export default RegisterAddress;
